export type PriceQuoteInput = {
  sku: string;
  quantity: number;
  options: Record<string, unknown>;
  deliveryPromise?: number | string;
  countryCode?: string;
  locale?: string;
};

export type PriceQuote = {
  hash: string;
  createdAt: string;
  expiresAt: string;
  countryCode: string;
};

const QUOTE_TTL_MS = 15 * 60 * 1000;

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
    .join(',')}}`;
}

function hashString(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function createPriceQuoteHash(input: PriceQuoteInput) {
  return hashString(stableStringify({
    sku: input.sku,
    quantity: Number(input.quantity || 1),
    options: input.options || {},
    deliveryPromise: input.deliveryPromise ?? 0,
    countryCode: String(input.countryCode || 'NL').toUpperCase(),
    locale: String(input.locale || 'nl').toLowerCase(),
  }));
}

export function createPriceQuote(input: PriceQuoteInput, now = new Date()): PriceQuote {
  const createdAt = now.toISOString();
  const expiresAt = new Date(now.getTime() + QUOTE_TTL_MS).toISOString();
  return {
    hash: createPriceQuoteHash(input),
    createdAt,
    expiresAt,
    countryCode: String(input.countryCode || 'NL').toUpperCase(),
  };
}

export function isPriceQuoteFresh(quote?: { expiresAt?: string } | null, now = new Date()) {
  if (!quote?.expiresAt) return false;
  const expiresAt = Date.parse(quote.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > now.getTime();
}

export const PRICE_CRITICAL_OPTION_IDS = new Set([
  'amount',
  'bedrukking',
  'binding',
  'clean_cut',
  'color',
  'copies',
  'custom_shape',
  'drillholes',
  'finish',
  'finish_left',
  'finish_right',
  'finish_top',
  'finish_bottom',
  'fold',
  'folded',
  'format',
  'frame',
  'height',
  'height_cm',
  'laminate',
  'material',
  'pages',
  'printtype',
  'printingmethod',
  'printing_side',
  'quantity',
  'rounded_corners',
  'size',
  'size_height',
  'size_width',
  'spot_finish',
  'spot_finish_back',
  'perforation',
  'variable_creasing_line',
  'type',
  'urgency',
  'usage',
  'width',
  'width_cm',
]);

export function isPriceCriticalOption(optionId: string) {
  return PRICE_CRITICAL_OPTION_IDS.has(optionId);
}
